/**
 * LoanSectionCard — tier 2 of the Drilldown Tab Header Standard (§8).
 *
 * The card every tab section lives in. Structurally this is the AI Reviews
 * panel generalized: optional tinted icon box, title + subtitle, right-aligned
 * card-scoped controls, a collapse chevron, a divider, and a body.
 *
 *   ╔═══════════════════════════════════════════════════╗
 *   ║ [icon]  Section title            [controls]  [v]  ║  ← 13px 16px
 *   ║         Section subtitle                          ║
 *   ║ ─────────────────────────────────────────────────  ║  ← 1px --border
 *   ║  body                                             ║  ← 15px 16px
 *   ╚═══════════════════════════════════════════════════╝
 *
 * Card-scoped controls ONLY. View toggles, filters, per-card search, counts,
 * expand/collapse — anything that changes what's inside this one card. If a
 * control changes the page or creates a record it belongs in the page header
 * (LoanTabHeader `primaryAction`), not here.
 *
 * The subtitle is optional; the divider is not, whenever a body follows. Counts
 * render as plain muted text (`14 total`), not badges — a badge reads as a state
 * that needs attention, and a row count doesn't.
 *
 * Section titles are <h2>. Never skip a level, and never pick a heading tag for
 * its size.
 */
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { Box, Collapse } from "@mui/material";
import PropTypes from "prop-types";
import { memo, useMemo, useState } from "react";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { TAB_TOKENS as T, TONE_STYLES } from "./tabTokens";

const LoanSectionCard = ({
  id,
  title,
  subTitle,
  icon,
  iconTone = "neutral",
  controls,
  count,
  collapsible = false,
  chevronStart = false,
  defaultOpen = true,
  tone = "neutral",
  disableBodyPadding = false,
  footer,
  children,
  sx,
  bodySx,
  headerSx,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = collapsible ? open : true;
  const toneStyle = TONE_STYLES[tone] || TONE_STYLES.neutral;
  const iconStyle = TONE_STYLES[iconTone] || TONE_STYLES.neutral;

  const hasHeader = Boolean(title || controls || count != null);
  const hasBody = Boolean(children);

  const rootStyles = useMemo(
    () => ({
      background: tone === "neutral" ? T.color.surface : toneStyle.fill,
      border: `1px solid ${tone === "neutral" ? T.color.border : toneStyle.line}`,
      borderRadius: T.radius.card,
      boxShadow: T.shadow.card,
      overflow: "hidden",
      ...sx,
    }),
    [tone, toneStyle, sx],
  );

  return (
    <Box component="section" id={id} sx={rootStyles}>
      {hasHeader && (
        <Flx
          ac
          onClick={collapsible ? () => setOpen((v) => !v) : undefined}
          sx={{
            gap: T.space.controlGap,
            padding: T.space.cardHeaderPad,
            cursor: collapsible ? "pointer" : "default",
            userSelect: collapsible ? "none" : "auto",
            borderBottom:
              hasBody && expanded ? `1px solid ${T.color.border}` : "none",
            transition: "background-color 100ms ease",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            ...(collapsible && {
              "&:hover": { background: T.color.surfaceSunk },
            }),
            [T.bp.controlsWrap]: {
              flexWrap: "wrap",
              gap: T.space.actionGap,
            },
            ...headerSx,
          }}
        >
          {/* Leading chevron: points right when closed, down when open. */}
          {collapsible && chevronStart && (
            <ExpandMoreRounded
              sx={{
                fontSize: 20,
                color: T.color.muted,
                flexShrink: 0,
                transition: "transform 150ms ease",
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                transform: expanded ? "none" : "rotate(-90deg)",
              }}
            />
          )}

          {icon && (
            <Flx
              center
              sx={{
                flexShrink: 0,
                width: 30,
                height: 30,
                borderRadius: T.radius.sm,
                background: iconTone === "neutral" ? T.color.surfaceSunk : iconStyle.fill,
                border: `1px solid ${iconTone === "neutral" ? T.color.border : iconStyle.line}`,
                color: iconTone === "neutral" ? T.color.ink3 : iconStyle.ink,
                "& svg": { fontSize: 16 },
              }}
            >
              {icon}
            </Flx>
          )}

          <Flx column sx={{ gap: "2px", minWidth: 0, flex: 1 }}>
            {title && (
              <Box component="h2" sx={{ ...T.type.sectionTitle, margin: 0 }}>
                {title}
              </Box>
            )}
            {subTitle && <Txt sx={T.type.sectionSubtitle}>{subTitle}</Txt>}
          </Flx>

          {/* Counts are muted text, never badges. */}
          {count != null && (
            <Txt sx={{ ...T.type.meta, flexShrink: 0 }}>{count}</Txt>
          )}

          {controls && (
            // Controls own their clicks — interacting with one must not toggle
            // the collapse.
            <Flx
              ac
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ gap: T.space.actionGap, flexShrink: 0 }}
            >
              {controls}
            </Flx>
          )}

          {collapsible && !chevronStart && (
            <ExpandMoreRounded
              sx={{
                fontSize: 20,
                color: T.color.muted,
                flexShrink: 0,
                transition: "transform 150ms ease",
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                transform: expanded ? "rotate(180deg)" : "none",
              }}
            />
          )}
        </Flx>
      )}

      {hasBody && (
        <Collapse in={expanded} unmountOnExit={false}>
          <Box
            sx={{
              padding: disableBodyPadding ? 0 : T.space.cardPad,
              ...bodySx,
            }}
          >
            {children}
          </Box>
          {footer && (
            <Box
              sx={{
                borderTop: `1px solid ${T.color.border}`,
                padding: T.space.cardHeaderPad,
                background: T.color.surfaceSunk,
              }}
            >
              {footer}
            </Box>
          )}
        </Collapse>
      )}
    </Box>
  );
};

LoanSectionCard.propTypes = {
  /** DOM id on the <section> — for a tab whose sub-views are page anchors. */
  id: PropTypes.string,
  /** Section name. Sentence case. Renders as <h2>. */
  title: PropTypes.node,
  /** Optional supporting line. */
  subTitle: PropTypes.node,
  /** Optional icon, rendered in a tinted rounded square. */
  icon: PropTypes.node,
  iconTone: PropTypes.oneOf(["neutral", "danger", "warn", "ok", "brand"]),
  /** Card-scoped controls only — toggles, filters, per-card actions. */
  controls: PropTypes.node,
  /** Row count etc. Rendered as muted text: `14 total`. */
  count: PropTypes.node,
  collapsible: PropTypes.bool,
  /** Put the collapse chevron before the title instead of at the far end. */
  chevronStart: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  /** Status treatment for the whole card. Use sparingly. */
  tone: PropTypes.oneOf(["neutral", "danger", "warn", "ok", "brand"]),
  /** Drop body padding — for a card whose body is a full-bleed table. */
  disableBodyPadding: PropTypes.bool,
  /** Optional sunk footer strip below the body. */
  footer: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
  bodySx: PropTypes.object,
  headerSx: PropTypes.object,
};

export default memo(LoanSectionCard);
